package com.example.mystybag.e5AiSearch.helpers

import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException

internal data class TokenInfo(val id: Int, val score: Float)

/**
 * Web implementation of [E5Tokenizer] using tokenizer.json (Unigram) loaded via fetch.
 * tokenizer.json must be copied to the web output (e.g. by webpack CopyWebpackPlugin).
 */
internal class WebTokenizer(
    private val vocab: Map<String, TokenInfo>,
    private val maxTokenLength: Int,
    private val unkId: Int,
    private val bosId: Int,
    private val eosId: Int,
    private val padId: Int
) : E5Tokenizer {

    override fun encode(text: String, maxLen: Int): Tokens {
        val norm = normalizeForSentencePiece(text)
        val metaspace = metaspace(norm)
        val pieceIds = unigramEncode(metaspace)

        val ids = LongArray(maxLen) { padId.toLong() }
        val mask = LongArray(maxLen) { 0L }
        val typeIds = LongArray(maxLen) { 0L }

        var pos = 0
        fun put(id: Int) {
            if (pos >= maxLen) return
            ids[pos] = id.toLong()
            mask[pos] = 1L
            typeIds[pos] = 0L
            pos++
        }

        put(bosId)
        val limit = maxLen - 1
        for (id in pieceIds) {
            if (pos >= limit) break
            put(id)
        }
        put(eosId)

        return Tokens(ids, mask, typeIds)
    }

    private fun unigramEncode(input: String): IntArray {
        val n = input.length
        val negInf = -1e30f

        val best = FloatArray(n + 1) { negInf }
        val prev = IntArray(n + 1) { -1 }
        val prevId = IntArray(n + 1) { -1 }

        best[0] = 0f

        for (i in 0 until n) {
            val base = best[i]
            if (base <= negInf / 2) continue

            var anyMatch = false
            val maxL = minOf(maxTokenLength, n - i)

            for (l in 1..maxL) {
                val sub = input.substring(i, i + l)
                val info = vocab[sub] ?: continue

                anyMatch = true
                val j = i + l
                val score = base + info.score
                if (score > best[j]) {
                    best[j] = score
                    prev[j] = i
                    prevId[j] = info.id
                }
            }

            if (!anyMatch) {
                val j = i + 1
                val score = base + 0f
                if (score > best[j]) {
                    best[j] = score
                    prev[j] = i
                    prevId[j] = unkId
                }
            }
        }

        var cur = n
        val tmp = IntArray(n + 5)
        var cnt = 0
        while (cur > 0) {
            val p = prev[cur]
            val id = prevId[cur]
            if (p < 0 || id < 0) return intArrayOf(unkId)
            tmp[cnt++] = id
            cur = p
        }

        val out = IntArray(cnt)
        for (k in 0 until cnt) out[k] = tmp[cnt - 1 - k]
        return out
    }

    companion object {
        suspend fun load(tokenizerUrl: String): WebTokenizer = suspendCancellableCoroutine { cont ->
            val fullUrl = js("(function(rel){ var b = typeof document !== 'undefined' && document.baseURI; return b ? new URL(rel, b).href : rel; })")(tokenizerUrl) as String
            E5Platform.log("E5TOK", "WebTokenizer.load: tokenizerUrl='$tokenizerUrl' resolved fullUrl='$fullUrl'")
            val promise = js("(function(url){ return fetch(url); })")(tokenizerUrl)
            promise.then(
                { response: dynamic ->
                    val status = response.status as Int
                    val respUrl = (response.url as? String) ?: tokenizerUrl
                    E5Platform.log("E5TOK", "WebTokenizer.load: fetch response status=$status url=$respUrl")
                    if (status != 200) {
                        cont.resumeWithException(Throwable("tokenizer.json fetch failed: status=$status url=$respUrl"))
                        return@then
                    }
                    response.json().then(
                        { json: dynamic ->
                            try {
                                val tok = parseTokenizerJson(json)
                                cont.resume(tok)
                            } catch (e: Throwable) {
                                cont.resumeWithException(e)
                            }
                        },
                        { err: dynamic ->
                            cont.resumeWithException(Throwable("tokenizer.json parse failed: ${err?.message ?: err}"))
                        }
                    )
                },
                { err: dynamic ->
                    cont.resumeWithException(Throwable("tokenizer.json fetch failed: ${err?.message ?: err}"))
                }
            )
        }

        private fun parseTokenizerJson(json: dynamic): WebTokenizer {
            val model = json.model ?: throw IllegalStateException("tokenizer.json: missing model")
            val unkId = (model.unk_id as Double).toInt()
            val vocabArr = model.vocab ?: throw IllegalStateException("tokenizer.json: missing model.vocab")

            val tokenToInfo = mutableMapOf<String, TokenInfo>()
            var maxLen = 0
            val vocabLength = (vocabArr.length as Int)
            for (idx in 0 until vocabLength) {
                val pair = vocabArr[idx]
                val token = pair[0] as String
                val score = (pair[1] as Double).toFloat()
                tokenToInfo[token] = TokenInfo(id = idx, score = score)
                if (token.length > maxLen) maxLen = token.length
            }

            fun requireId(token: String): Int =
                tokenToInfo[token]?.id ?: throw IllegalStateException("Missing token in vocab: $token")

            val bosId = requireId("<s>")
            val padId = requireId("<pad>")
            val eosId = requireId("</s>")

            E5Platform.log("E5TOK", "Web tokenizer loaded vocab=${tokenToInfo.size} maxTokenLen=$maxLen")
            return WebTokenizer(
                vocab = tokenToInfo,
                maxTokenLength = maxLen,
                unkId = unkId,
                bosId = bosId,
                eosId = eosId,
                padId = padId
            )
        }
    }
}

private fun normalizeForSentencePiece(s: String): String =
    s.trim().replace(Regex("\\s+"), " ")

private fun metaspace(s: String): String {
    val withPrefix = if (s.isEmpty() || s[0] == ' ') s else " $s"
    return withPrefix.replace(' ', '▁')
}
